import type { ReactNode } from 'react';
import {
  Tab,
  TabGroup,
  TabList,
  TabPanel,
  TabPanels,
} from '@plerionhq/design-system';
import type { AccessGrant } from '../../types';
import { mechanismCarriesPolicyDocument } from '../../lib/resource-policy';
import { AccessGrantSlideOverHeader } from './access-grant-slide-over-header';
import { AccessGrantSummaryPanel } from './access-grant-summary-panel';
import { AccessGrantOverviewTab } from './access-grant-overview-tab';
import { AccessGrantAuditTab } from './access-grant-audit-tab';
import { AccessGrantPermissionsTab } from './access-grant-permissions-tab';
import { AccessGrantPolicyTab } from './access-grant-policy-tab';

import { useTranslation } from 'react-i18next';

type AccessGrantSlideOverContentProperties = {
  grant: AccessGrant;
  slideOverId: string;
};

export function AccessGrantSlideOverContent({
  grant,
  slideOverId,
}: AccessGrantSlideOverContentProperties) {
  const { t } = useTranslation('accessGrants');
  // The Policy tab renders the resource's policy document; mechanisms that share
  // through something other than a policy (RAM shares, attribute shares) have no
  // document to show, so we hide the tab entirely rather than render an empty one.
  const tabs: Array<{ label: string; content: ReactNode }> = [
    {
      label: t('common:overview'),
      content: <AccessGrantOverviewTab grant={grant} />,
    },
    {
      label: t('permissions'),
      content: <AccessGrantPermissionsTab grant={grant} />,
    },
    ...(mechanismCarriesPolicyDocument(grant.mechanism)
      ? [
          {
            label: t('policy'),
            content: <AccessGrantPolicyTab grant={grant} />,
          },
        ]
      : []),
    {
      label: t('accessReview'),
      // key on grant.id remounts the tab (resetting its locally seeded field
      // state) if the slide-over is ever reused for a different grant, so an
      // edit can't PATCH the wrong grant.
      content: <AccessGrantAuditTab key={grant.id} grant={grant} />,
    },
  ];

  return (
    <div className="flex h-full flex-col bg-gray-50 dark:bg-gray-900">
      <AccessGrantSlideOverHeader slideOverId={slideOverId} />
      <div className="flex-1 overflow-y-auto p-5">
        <div className="flex flex-col gap-4">
          <AccessGrantSummaryPanel grant={grant} />
          <TabGroup>
            {/* Transparent so the strip's grey doesn't show at the tabs' rounded corners. */}
            <TabList className="bg-transparent">
              {tabs.map((tab) => (
                <Tab
                  key={tab.label}
                  data-testid={`grant-tab-${tab.label
                    .toLowerCase()
                    .replace(/\s+/g, '-')}`}
                >
                  {tab.label}
                </Tab>
              ))}
            </TabList>
            <TabPanels>
              {tabs.map((tab) => (
                <TabPanel key={tab.label}>{tab.content}</TabPanel>
              ))}
            </TabPanels>
          </TabGroup>
        </div>
      </div>
    </div>
  );
}
